import React, { useState, useEffect, useRef } from "react";
import { Indicateur } from "../types/Indicateur";
import AuthentifiedPage from "./AuthentifiedPage";
import SystemeAlerte from "../components/SystemeAlerte";
import AddDocumentMenuWrapper from "../components/AddDocumentMenuWrapper";
import { useNavigate } from "react-router-dom";
import "../styles/DocumentListeAgentPage.css";
import "../styles/Indicateur.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Domaine, SousDomaine, SousSousDomaine } from "../types/Domaine";
import CollecteLoadingMessage from "../components/CollecteLoadingMessage";
import { useAuth } from "../context/AuthContext";
import { indicateurService } from "../services/IndicateurService";
import { predictionService, PredictionValue } from "../services/PredictionService";
import { ileService } from "../services/IleService";
import { regionService } from "../services/RegionService";
import { communeService } from "../services/CommuneService";
import { localiteService } from "../services/LocaliteService";
import { Ile } from "../types/Ile";
import { Region } from "../types/Region";
import { Commune } from "../types/Commune";
import { Localite } from "../types/Localite";

// Badge pour le statut
const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <span className={`user-btn-status ${active ? "actif" : "inactif"}`}>
    <span className="status-indicator"></span>
    {active ? "Actif" : "Inactif"}
  </span>
);

const IndicateurListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();

  const [indicateurs, setIndicateurs] = useState<Indicateur[]>([]);
  const [query, setQuery] = useState("");
  const [statutFilter, setStatutFilter] = useState<string>("");
  const [selectedIndicateur, setSelectedIndicateur] = useState<Indicateur | null>(null);
  const [buttonClicked, setButtonClicked] = useState<"createIndicator" | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState<{ id: number; x: number; y: number } | null>(null);
  const [previsionPopupOpen, setPrevisionPopupOpen] = useState(false);
  const [predictionMethod, setPredictionMethod] = useState<"mean" | "linear">("mean");
  const [targetMonth, setTargetMonth] = useState<string>(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
  const [predictions, setPredictions] = useState<PredictionValue[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [iles, setIles] = useState<Ile[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [localites, setLocalites] = useState<Localite[]>([]);
  
  const [showPopup, setShowPopup] = useState(false);
  const [selectedIle, setSelectedIle] = useState<number | "">("");
  const [selectedRegion, setSelectedRegion] = useState<number | "">("");
  const [selectedCommune, setSelectedCommune] = useState<number | "">("");
  const [selectedLocalite, setSelectedLocalite] = useState<number | "">("");

  const [selectedIndicateurId, setSelectedIndicateurId] = useState<number | null>(null);
  const [predictionLocality, setPredictionLocality] = useState<{ idIle?: number; idRegion?: number; idCommune?: number; idLocalite?: number }>({});

  // Charger les indicateurs depuis l'API
  useEffect(() => {
    loadIndicateurs();
  }, []);

  useEffect(() => {
      const fetchLocalities = async () => {
        try {
          const iles = await ileService.getAll(api);
          const regions = await regionService.getAllRegions(api);
          const communes = await communeService.getAll(api);
          const localites = await localiteService.getAll(api);
  
          setIles(iles);
          setRegions(regions);
          setCommunes(communes);
          setLocalites(localites);
        } catch (error) {
          console.error("Erreur lors de la récupération des données géographiques :", error);
        }
      };
  
      fetchLocalities();
    }, [api]);

  const loadIndicateurs = async () => {
    if (!api) {
      console.error("API non disponible");
      return;
    }

    try {
      setLoading(true);
      const data = await indicateurService.getAllIndicateurs(api);

      const mappedData = data.map((ind: any) => ({
        id: ind.idIndicateur || ind.id,
        nom: ind.nom,
        formule: ind.formule,
        sql: ind.sql || ind.formule,
        tableName: ind.tableName || ind.table_name || "N/A",
        valeur: ind.valeur || 0,
        isAccepted:
          ind.isAccepted !== undefined
            ? ind.isAccepted
            : ind.is_accepted !== undefined
            ? ind.is_accepted
            : false,
        idFenetre: ind.idFenetre ?? ind.id_fenetre ?? null,
        idSousFenetre: ind.idSousFenetre ?? ind.id_sous_fenetre ?? null,
        nomFenetre: ind.nomFenetre ?? ind.nom_fenetre ?? null,
        nomSousFenetre: ind.nomSousFenetre ?? ind.nom_sous_fenetre ?? null,
        unite: ind.unite ?? null,
        createdAt: ind.createdAt || ind.created_at
      }));

      setIndicateurs(mappedData);
    } catch (err: any) {
      console.error("Erreur lors du chargement des indicateurs:", err);
      alert("Impossible de charger les indicateurs");
      setIndicateurs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  }, [indicateurs]);

  // Fermer menu ou popup si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActionMenuOpen(null);
      }
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) setPrevisionPopupOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);  

  const toggleActive = async (id: number) => {

    if (!id || id === 0) {
      console.error("ID invalide:", id);
      alert("ID d'indicateur invalide");
      return;
    }

    if (!api) {
      console.error("API non disponible");
      alert("API non disponible");
      return;
    }

    const indicateur = indicateurs.find(i => i.id === id);

    if (!indicateur) {
      console.error("Indicateur introuvable avec l'ID:", id);
      alert("Indicateur introuvable");
      return;
    }

    try {
      setLoading(true);

      let response;

      if (indicateur.isAccepted) {
        response = await indicateurService.declinerIndicateur(api, id);
      } else {
        response = await indicateurService.accepterIndicateur(api, id);
      }

      if (!response.success) {
        throw new Error(response.message);
      }

      setIndicateurs(prev =>
        prev.map(i =>
          i.id === id ? { ...i, isAccepted: !i.isAccepted } : i
        )
      );

      setActionMenuOpen(null);

    } catch (err: any) {
      console.error("Erreur lors de la mise à jour:", err);
      alert(err.message || "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDomain = (domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => {
    setShowMenu(false);
    setLoading(true);
    const safeDomain = { ...domain, icon: undefined };
    const safeSousDomain = sousDomain ? { ...sousDomain, icon: undefined } : undefined;
    const safeSousSousDomain = sousSousDomain ? { ...sousSousDomain, icon: undefined } : undefined;
    setTimeout(() => {
      setLoading(false);
      navigate("/create-indicateur", {
        state: { domain: safeDomain, sousDomain: safeSousDomain, sousSousDomain: safeSousSousDomain, mode: "createIndicator" },
      });
    }, 500);
  };

  const handleCreateIndicator = (domain:Domaine, sousDomain?:SousDomaine, sousSousDomain?: SousSousDomaine) => {
    handleSelectDomain(domain, sousDomain, sousSousDomain);
  };

  const calculatePredictions = async () => {
    if (!selectedIndicateurId) return;

    setLoadingPredictions(true);
    try {
      const results = predictionMethod === "mean"
        ? await predictionService.getPredictionsByAverage(
            api,
            selectedIndicateurId,
            targetMonth,
            predictionLocality.idIle,
            predictionLocality.idRegion,
            predictionLocality.idCommune,
            predictionLocality.idLocalite
          )
        : await predictionService.getPredictionsByRegression(
            api,
            selectedIndicateurId,
            targetMonth,
            predictionLocality.idIle,
            predictionLocality.idRegion,
            predictionLocality.idCommune,
            predictionLocality.idLocalite
          );
      
      console.log("Results:", JSON.stringify(results));
      setPredictions(results);
    } catch (error: any) {
      console.error("Erreur lors du calcul des prédictions:", error);
      alert("Erreur lors du calcul des prédictions");
    } finally {
      setLoadingPredictions(false);
    }
  };

  // Filtrage
  const filtered = indicateurs.filter(ind => {
    const matchQuery = ind.nom.toLowerCase().includes(query.toLowerCase());
    const matchStatut =
      statutFilter === "" ||
      (statutFilter === "actif" ? ind.isAccepted : !ind.isAccepted);
    return matchQuery && matchStatut;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  // Afficher détails indicateur
  if (selectedIndicateur) {
    return (
      <AuthentifiedPage>
        <SystemeAlerte
          indicateurs={[selectedIndicateur]}
          initialIndicateurId={selectedIndicateur.id}
          onBack={() => setSelectedIndicateur(null)}
        />
      </AuthentifiedPage>
    );
  }

  return (
    <AuthentifiedPage>
      <main className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 className="text-success fw-bold">Liste des indicateurs</h4>
          <div className="button-group">
            <button className="btn-import" onClick={() => { setButtonClicked("createIndicator"); setShowMenu(true); }}>
              <i className="bi bi-bar-chart-line"></i> Créer un indicateur
            </button>
          </div>
        </div>

        <div className="filters-bar mb-3">
          <div className="input-wrapper">
            <span className="search-icon"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher un indicateur..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <select className="custom-select" value={statutFilter} onChange={e => setStatutFilter(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="actif">Actif</option>
            <option value="inactif">Inactif</option>
          </select>
        </div>

        {showMenu && (
          <AddDocumentMenuWrapper
            onSelect={handleSelectDomain}
            onCreateIndicator={handleCreateIndicator}
            onCreateForm={() => setShowMenu(false)}
            onCreateRapport={() => setShowMenu(false)}
            onClose={() => setShowMenu(false)}
            action={buttonClicked}
          />
        )}

        <div className="table-responsive shadow-sm">
          <table className="table table-hover align-middle">
            <thead className="table-success">
              <tr>
                <th>Nom</th>
                <th>Fenêtre</th>
                <th>Sous-Fenêtre</th>
                <th>Formule</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    Chargement...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    Aucun indicateur trouvé
                  </td>
                </tr>
              ) : paginated.map(ind => {
                  return (
                    <tr key={ind.id}>
                      <td>{ind.nom}</td>
                      <td>{ind.nomFenetre || "-"}</td>
                      <td>{ind.nomSousFenetre || "-"}</td>
                      <td style={{ maxWidth: "300px", wordBreak: "break-word" }}>{ind.formule}</td>
                      <td><StatusBadge active={!!ind.isAccepted} /></td>
                      <td>
                        <button
                          className="btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                            const idToUse = ind.id!;
                            setActionMenuOpen(
                              actionMenuOpen?.id === idToUse
                                ? null
                                : { id: idToUse, x: rect.right, y: rect.bottom }
                            );
                          }}
                        >
                          &#x22EE;
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {actionMenuOpen && (
          <div
            ref={menuRef}
            className="action-menu-floating"
            style={{
              position: "fixed",
              top: `${actionMenuOpen.y}px`,
              left: `${actionMenuOpen.x - 150}px`,
              width: "180px",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <button 
              onClick={() => {
                toggleActive(actionMenuOpen.id);
              }} 
              style={{ padding: "8px", border: "none", background: "none", textAlign: "left", cursor: "pointer" }}
              disabled={loading}
            >
            {indicateurs.find(i => i.id === actionMenuOpen.id)?.isAccepted ? "Désactiver" : "Activer"}
            </button>
            {(user?.roleRank ?? 0) >= 4 && (
              <button 
                onClick={() => { 
                  const ind = indicateurs.find(i => i.id === actionMenuOpen.id);
                  if (!ind) {
                    console.error("Indicateur introuvable");
                    alert("Indicateur introuvable");
                    return;
                  }
                  setSelectedIndicateur(ind); 
                  setActionMenuOpen(null); 
                }} 
                style={{ padding: "8px", border: "none", background: "none", textAlign: "left", cursor: "pointer" }}
                disabled={loading}
              >
                Configurer le seuil
              </button>
            )}

            <button
              onClick={() => {
                if (!actionMenuOpen?.id) return;

                setSelectedIndicateurId(actionMenuOpen.id);
                setPredictions([]);
                setPredictionLocality({});
                setPrevisionPopupOpen(true);
                setActionMenuOpen(null);
              }}
              style={{ padding: "8px", border: "none", background: "none", textAlign: "left", cursor: "pointer" }}
              disabled={loading}
            >
              Prévision
            </button>

          </div>
        )}

        {previsionPopupOpen && (
          <div>
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.4)",
                zIndex: 9998,
              }}
              onClick={() => setPrevisionPopupOpen(false)}
            ></div>

            <div
              ref={popupRef}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "90%",
                maxWidth: "550px",
                maxHeight: "80vh",
                overflowY: "auto",
                backgroundColor: "white",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                padding: "1rem",
                zIndex: 10000,
              }}
            >
              <h5 className="fw-bold mb-3">Calcul de prédiction</h5>

              <div className="mb-3">
                <label className="form-label fw-bold">Mois cible</label>
                <input
                  type="month"
                  value={targetMonth}
                  onChange={(e) => setTargetMonth(e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Localité</label>
                <div className="row g-2">
                  <div className="col-sm-6 col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={predictionLocality.idIle ?? ''}
                      onChange={e => setPredictionLocality(prev => ({ ...prev, idIle: e.target.value ? Number(e.target.value) : undefined }))}
                    >
                      <option value="">Îles</option>
                      {iles.map(i => <option key={i.idIle} value={i.idIle}>{i.nomIle}</option>)}
                    </select>
                  </div>

                  <div className="col-sm-6 col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={predictionLocality.idRegion ?? ''}
                      onChange={e => setPredictionLocality(prev => ({ ...prev, idRegion: e.target.value ? Number(e.target.value) : undefined }))}
                    >
                      <option value="">Régions</option>
                      {regions.filter(r => !predictionLocality.idIle || r.idIle === predictionLocality.idIle).map(r => <option key={r.idRegion} value={r.idRegion}>{r.nomRegion}</option>)}
                    </select>
                  </div>

                  <div className="col-sm-6 col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={predictionLocality.idCommune ?? ''}
                      onChange={e => setPredictionLocality(prev => ({ ...prev, idCommune: e.target.value ? Number(e.target.value) : undefined }))}
                    >
                      <option value="">Communes</option>
                      {communes.filter(c => !predictionLocality.idRegion || c.idRegion === predictionLocality.idRegion).map(c => <option key={c.idCommune} value={c.idCommune}>{c.nomCommune}</option>)}
                    </select>
                  </div>

                  <div className="col-sm-6 col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={predictionLocality.idLocalite ?? ''}
                      onChange={e => setPredictionLocality(prev => ({ ...prev, idLocalite: e.target.value ? Number(e.target.value) : undefined }))}
                    >
                      <option value="">Localités</option>
                      {localites.filter(l => !predictionLocality.idCommune || l.idCommune === predictionLocality.idCommune).map(l => <option key={l.idLocalite} value={l.idLocalite}>{l.nomLocalite}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Méthode de prédiction</label>
                <div className="d-flex gap-2">
                  <button
                    className={`btn btn-sm ${predictionMethod === "mean" ? "btn-success" : "btn-outline-secondary"}`}
                    onClick={() => setPredictionMethod("mean")}
                  >
                    Moyenne
                  </button>
                  <button
                    className={`btn btn-sm ${predictionMethod === "linear" ? "btn-success" : "btn-outline-secondary"}`}
                    onClick={() => setPredictionMethod("linear")}
                  >
                    Régression
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={calculatePredictions}
                    disabled={loadingPredictions}
                  >
                    {loadingPredictions ? "Calcul..." : "Calculer"}
                  </button>
                </div>
              </div>

              {predictions.length > 0 && (
                <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                  <h6 className="fw-bold mb-2">Résultats</h6>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Indicateur</th>
                          <th>Mois</th>
                          <th>Valeur prédite</th>
                        </tr>
                      </thead>
                      <tbody>
                        {predictions.map((pred, idx) => (
                          <tr key={idx}>
                            <td>{pred.nom}</td>
                            <td>{pred.month}</td>
                            <td className="fw-bold">{pred.moyenne.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end mt-3 gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setPrevisionPopupOpen(false)}>Fermer</button>
              </div>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <small className="text-muted">{paginated.length} indicateurs affichés sur {filtered.length}</small>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map(num => (
                  <li key={num} className={`page-item ${num === currentPage ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage(num)}>{num}</button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}

        {showPopup && (
          <div className="popup-overlay">
            <div className="popup-content card p-3">
              <h5>Sélectionner une localisation</h5>

              <div className="mb-2">
                <label>Ile</label>
                <select
                  className="form-select"
                  value={selectedIle}
                  onChange={(e) => setSelectedIle(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {iles.map((i) => (
                    <option key={i.idIle} value={i.idIle}>{i.nomIle}</option>
                  ))}
                </select>
              </div>

              <div className="mb-2">
                <label>Région</label>
                <select
                  className="form-select"
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {regions.map((r) => (
                    <option key={r.idRegion} value={r.idRegion}>{r.nomRegion}</option>
                  ))}
                </select>
              </div>

              <div className="mb-2">
                <label>Commune</label>
                <select
                  className="form-select"
                  value={selectedCommune}
                  onChange={(e) => setSelectedCommune(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {communes.map((c) => (
                    <option key={c.idCommune} value={c.idCommune}>{c.nomCommune}</option>
                  ))}
                </select>
              </div>

              <div className="mb-2">
                <label>Localité</label>
                <select
                  className="form-select"
                  value={selectedLocalite}
                  onChange={(e) => setSelectedLocalite(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {localites.map((l) => (
                    <option key={l.idLocalite} value={l.idLocalite}>{l.nomLocalite}</option>
                  ))}
                </select>
              </div>    
              <div className="mt-3 text-end">
                <center> 
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      console.log("Localisation choisie :", {
                        idIndicateur: selectedIndicateurId,
                        ile: selectedIle,
                        region: selectedRegion,
                        commune: selectedCommune,
                        localite: selectedLocalite
                      });

                      setShowPopup(false);
                    }}
                  >
                    Choisir
                  </button>
                </center>    
              </div>
            </div>
          </div>
        )}

        {loading && <CollecteLoadingMessage message="Chargement..." />}
      </main>
    </AuthentifiedPage>
  );
};

export default IndicateurListPage;